import baseAPI, { parseAPI } from '../app/axios';

export default class Organization {
  static async getOrganization(orgId) {
    if (!orgId) throw new Error('getOrganization: orgId is empty');

    const query = {
      where: {
        objectId: orgId,
      },
      include:
        'conversation.lastMessage,conversation,members,owner,perm,accounting,owner',
      keys: 'features,redirectOptions,accounting,category,displayName,icon,members,owner,conversation,owner.picture,owner.firstName,owner.lastName,accounting.message,accounting.customer,perm,status,_acl,_wperm,_rperm',
      order: 'displayName',
      _method: 'GET',
    };

    const { results } = await parseAPI.post('/classes/Organization', query);
    return (results || [])[0];
  }

  static async saveDefaultTemplate(
    orgId,
    templateId,
    templateType,
    name,
    elements,
    expiredAt,
  ) {
    if (!orgId) throw new Error('saveDefaultTemplate: orgId is Empty');
    if (!templateType)
      throw new Error('saveDefaultTemplate: templateType is Empty');
    if (!name) throw new Error('saveDefaultTemplate: name is Empty');
    if (elements.length <= 0)
      throw new Error('saveDefaultTemplate: elements is Empty');

    const payload = {
      template: {
        className: 'Template',
        templateType,
        name,
        expiredAt,
        data: {
          elements: elements.map(element => {
            const temp = { ...element };
            delete temp.id;
            temp.buttons = temp.buttons.map(button => {
              const tempButton = { ...button };
              delete tempButton.id;
              return tempButton;
            });
            return temp;
          }),
          templateType,
        },
        organization: {
          __type: 'Pointer',
          className: 'Organization',
          objectId: orgId,
        },
        hidden: false,
        templateId,
        version: 2,
      },
    };

    return parseAPI.post('/functions/publishTemplate', payload);
  }

  static async getDefaultTemplates(orgId, templateType, whereOptions, options) {
    return parseAPI.post('/functions/queryTemplate', {
      where: {
        organization: {
          __type: 'Pointer',
          className: 'Organization',
          objectId: orgId,
        },
        templateType,
        vendor: { $ne: true },
        ...whereOptions,
      },
      skip: 0,
      limit: 50,
      sort: {
        createdAt: -1,
      },
      group: true,
      ...options,
    });
  }

  static async deleteDefaultTemplate(orgId, templateId) {
    return parseAPI.post('/functions/removeTemplate', {
      templateId,
      organization: {
        __type: 'Pointer',
        className: 'Organization',
        objectId: orgId,
      },
    });
  }

  static async getAllServices(orgId) {
    const response = await baseAPI.get(`/services/${orgId}`);
    return response;
  }

  static async getService(orgId, provider) {
    const response = await baseAPI.get(`/services/${orgId}/${provider}`);
    return response;
  }

  static async getIntegration(orgId) {
    if (!orgId) throw new Error('getIntegration: orgId is Empty');

    const query = {
      where: {
        organization: {
          __type: 'Pointer',
          className: 'Organization',
          objectId: orgId,
        },
      },
      _method: 'GET',
      include: 'platform',
    };

    const response = await parseAPI.post(`/classes/Integration`, query);
    return response?.results || [];
  }

  static async getGA4EventDomain(orgId) {
    if (!orgId) throw new Error('getGA4EventDomain: orgId is Empty');

    const response = await baseAPI.get(`/ga4_event/domain?orgId=${orgId}`);
    return response?.domain || '';
  }

  static async getEnableEC(orgId) {
    if (!orgId) throw new Error('getEnableEC: orgId is Empty');

    const response = await baseAPI.get(
      `/ec/retargeting/orgEnabled?orgId=${orgId}`,
    );
    return response?.enabledEC || '';
  }

  static async getWhatsappTemplateList({
    orgId,
    limit = 50,
    skip = 0,
    name,
    contentType,
    status,
    skipSync,
  }) {
    let query = `${orgId}?limit=${limit}&skip=${skip}`;
    if (status) {
      query += `&status=${status}`;
    }

    if (skipSync) {
      query += `&skipSync=${skipSync}`;
    }

    if (name) {
      query += `&name=${name}`;
    }
    if (contentType) {
      query += `&contentType=${contentType}`;
    }

    const response = await baseAPI.get(`/whatsapp/templates/${query}`);

    return response;
  }

  static async getOrganizationMembers({
    org,
    keyword,
    memberId,
    memberIds,
    limit,
  }) {
    const mainQuery = {
      order: 'firstName',
      limit,
      _method: 'GET',
    };

    if (org) {
      mainQuery.where = {
        $or: [
          {
            $relatedTo: {
              object: {
                __type: 'Pointer',
                className: 'Membership',
                objectId: org.members.objectId,
              },
              key: 'users',
            },
          },
        ],
      };
    }

    if (keyword) {
      mainQuery.where = {
        $or: [
          {
            $relatedTo: {
              object: {
                __type: 'Pointer',
                className: 'Membership',
                objectId: org.members.objectId,
              },
              key: 'users',
            },
            firstName: {
              $regex: keyword,
              $options: 'i',
            },
          },
          {
            $relatedTo: {
              object: {
                __type: 'Pointer',
                className: 'Membership',
                objectId: org.members.objectId,
              },
              key: 'users',
            },
            lastName: {
              $regex: keyword,
              $options: 'i',
            },
          },
        ],
      };
    }

    if (memberId) {
      mainQuery.where = { objectId: memberId };
    }

    if (memberIds) {
      mainQuery.where = { objectId: { $in: memberIds } };
    }

    if (limit) {
      mainQuery.limit = limit;
    }

    const { results } = await parseAPI.post('/classes/_User', mainQuery);
    return results;
  }

  static async findTagV3(tagName = '', orgId) {
    if (!orgId) {
      throw new Error('findTag: orgId undefined');
    }
    const query = {
      where: {
        organization: {
          className: 'Organization',
          objectId: orgId,
        },
        name: {
          $options: 'i',
          $regex: tagName,
        },
      },
      sort: { lastUsed: -1 },
      limit: 20,
      options: {
        output: ['name'],
      },
    };
    const response = await parseAPI.post('/functions/findTagV3', query);
    return response.result;
  }

  static async getNextResource(orgId, addonId) {
    if (!orgId) throw new Error('getNextResource: orgId is empty');

    const query = {
      where: {
        orgId,
        addonId,
      },
    };

    const response = await parseAPI.post(
      '/functions/inspectAddonResource',
      query,
    );
    return response?.result;
  }
}
